import { useState } from 'react';
import RichTextEditor from './RichTextEditor';

function QuoteGenerator({ 
    topic, 
    platform, 
    visualMode, 
    loading, 
    quote, 
    caption, 
    alignment, 
    isEditing, 
    onGenerate, 
    onEdit, 
    onQuoteChange, 
    onCaptionChange, 
    onAlignmentChange 
}) {
    const [copied, setCopied] = useState(false);

    const handleCopyCaption = () => {
        if (!caption) return;
        const plainText = caption.replace(/<[^>]*>?/gm, '');
        navigator.clipboard.writeText(plainText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const visualLabel = platform === 'linkedin' ? 'Post Heading' : 'Visual Quote';

    return (
        <div className="sa-card">
            <div className="sa-step-label">
                <div className="sa-step-num">2</div>
                <span className="sa-step-name">Studio: {platform === 'linkedin' ? 'LinkedIn' : 'Instagram'} Mode</span>
            </div>

            <div className="sa-card__title">Generate Post Package</div>
            <p style={{ color: 'var(--panacea-extra-light-color)', fontSize: '0.88rem', margin: '0 0 16px', lineHeight: 1.6 }}>
                Topic: <strong style={{ color: 'var(--panacea-primary-color)', textTransform: 'capitalize' }}>{topic}</strong>
                &nbsp;· {platform === 'linkedin' ? 'Pro Heading + Long Post' : 'Viral Quote + Punchy Caption'}
            </p>

            <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
                <button
                    className="sa-generate-btn"
                    onClick={onGenerate}
                    disabled={loading}
                    style={{ flex: 1 }}
                >
                    {loading ? (
                        <>
                            <div className="sa-spinner" />
                            Crafting Content...
                        </>
                    ) : (
                        <>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                            </svg>
                            {quote ? 'Regenerate Package' : 'Generate Post Package'}
                        </>
                    )}
                </button>
                
                {quote && (
                    <button className="sa-btn sa-btn--outline" onClick={onEdit}>
                        {isEditing ? 'View Preview' : 'Edit All Text'}
                    </button>
                )}
            </div>

            {/* --- Visual Content Section (Only for Upload Mode) --- */}
            {visualMode === 'upload' && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div className="sa-card__title" style={{ fontSize: '0.75rem', opacity: 0.6, margin: 0, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{visualLabel}</div>
                        {isEditing && (
                            <div className="sa-alignment-bar">
                                <button className={`sa-align-btn ${alignment === 'left' ? 'active' : ''}`} onClick={() => onAlignmentChange('left')} title="Align Left"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="17" y1="10" x2="3" y2="10" /><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="17" y1="18" x2="3" y2="18" /></svg></button>
                                <button className={`sa-align-btn ${alignment === 'center' ? 'active' : ''}`} onClick={() => onAlignmentChange('center')} title="Align Center"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="10" x2="6" y2="10" /><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="18" y1="18" x2="6" y2="18" /></svg></button>
                                <button className={`sa-align-btn ${alignment === 'right' ? 'active' : ''}`} onClick={() => onAlignmentChange('right')} title="Align Right"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="21" y1="10" x2="7" y2="10" /><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="21" y1="18" x2="7" y2="18" /></svg></button>
                            </div>
                        )}
                    </div>
                    
                    {quote && !isEditing ? (
                        <div className="sa-quote-box" style={{ marginBottom: 24, textAlign: alignment }}>
                            <span className="sa-quote-box__mark" style={{ left: alignment === 'right' ? 'auto' : -2, right: alignment === 'right' ? -2 : 'auto' }}>"</span>
                            <p className="sa-quote-box__text" dangerouslySetInnerHTML={{ __html: quote }} />
                        </div>
                    ) : quote && isEditing ? (
                        <RichTextEditor
                            value={quote}
                            onChange={onQuoteChange}
                            placeholder={`Edit your ${visualLabel.toLowerCase()}...`}
                            style={{ marginBottom: 24 }}
                        />
                    ) : null}
                </>
            )}

            {/* --- Caption Section --- */}
            {quote && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div className="sa-card__title" style={{ fontSize: '0.75rem', opacity: 0.6, margin: 0, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{platform === 'linkedin' ? 'LinkedIn Post' : 'Instagram Caption'}</div>
                        {!isEditing && (
                            <button className="sa-copy-btn" onClick={handleCopyCaption}>
                                {copied ? 'Copied!' : 'Copy Text'}
                            </button>
                        )}
                    </div>
                    
                    {!isEditing ? (
                        <div className="sa-caption-box">
                            <p style={{ whiteSpace: 'pre-wrap', margin: 0, lineHeight: 1.7 }}>{caption}</p>
                        </div>
                    ) : (
                        <RichTextEditor
                            value={caption}
                            onChange={onCaptionChange}
                            placeholder="Write your caption..."
                            style={{ height: '240px' }}
                        />
                    )}
                </>
            )}
        </div>
    );
}

export default QuoteGenerator;
